import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type MountWhenVisibleProps = {
  children: ReactNode;
  /** Margin around the viewport; larger values start loading before the section is visible. */
  rootMargin?: string;
  className?: string;
};

/**
 * Renders children only after the placeholder intersects the viewport, so lazy children
 * and their data hooks run only when the user scrolls near the section.
 */
export function MountWhenVisible({
  children,
  rootMargin = "320px 0px",
  className,
}: MountWhenVisibleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          setVisible(true);
        }
      },
      { root: null, rootMargin, threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} className={cn("w-full min-h-px", className)}>
      {visible ? children : null}
    </div>
  );
}
